import { TrendingUp } from 'lucide-react'
import { useMemo, useState } from 'react'
import CareSymptomTable from '@/components/common/care-symptom-table'
import CareTimelineChart, { type CareTimelineMode } from '@/components/common/care-timeline-chart'
import LoadingSpinner from '@/components/ui/icons/loading-spinner'
import { buildFixtureTrend, CARE_FIXTURES_ENABLED, fixtureDefinitions } from '@/lib/care-fixtures'
import { toCareTimeline } from '@/lib/care-series'
import { latestCheckIn, latestCheckInDate, toSectionSummaries } from '@/lib/care-symptoms'
import { cn } from '@/lib/utils'
import { useCareTrend } from '@/services/care'
import { useCareDefinitions } from '@/services/care-definitions'
import type { CareTrendPoint } from '@/types/care'

interface CareConsultationProps {
  patientHash: string | undefined
  className?: string
}

const MODES: { value: CareTimelineMode; label: string }[] = [
  { value: 'total', label: 'Menopause score' },
  { value: 'sections', label: 'All symptoms' }
]

function Panel({ children, className }: { children: React.ReactNode; className?: string }) {
  return <div className={cn('rounded-3xl border border-[#D1D9E2] p-8 font-poppins', className)}>{children}</div>
}

/**
 * The care visualisation: a patient's check-in history as a timeline, with the most recent
 * check-in's symptoms broken down by section underneath.
 *
 * Data comes from the Patient API's care trend read. Two things it cannot yet supply are handled
 * explicitly rather than papered over — a module has no definition to resolve names and maxima
 * from, and submissions carry no resolved scores until CD-94. Either one leaves the panel saying
 * so, because a chart drawn from nulls would read as "this patient scores zero".
 */
export default function CareConsultation({ patientHash, className }: CareConsultationProps) {
  const [mode, setMode] = useState<CareTimelineMode>('sections')

  // `isPending` stays true while the query is disabled, so it cannot stand in for "loading" on its
  // own — without `isFetching` a request with no patient hash would spin forever.
  const { data, isPending, isFetching, isError } = useCareTrend(patientHash)
  const live = useMemo(() => data?.data ?? [], [data])

  // Sample data stands in only where the patient has no care history to show — none recorded, or
  // none that could be read — and only in an environment that opted in. It is all or nothing: sample
  // check-ins are only ever drawn with the sample definition they were "answered" against, so a
  // fixture label can never land on a real check-in.
  //
  // Decided on `live` alone, never on `isError`: React Query keeps the last good `data` when a
  // background refetch fails and still reports `status: 'error'`, so `isError` can be true while real
  // history is on screen. A failed first load leaves `data` undefined, which `live.length === 0`
  // already covers.
  const usingFixtures = CARE_FIXTURES_ENABLED && !!patientHash && live.length === 0

  const points: CareTrendPoint[] = useMemo(
    () => (usingFixtures && patientHash ? buildFixtureTrend(patientHash) : live),
    [usingFixtures, patientHash, live]
  )

  // Every point in a page belongs to the module it was answered against; the timeline plots one
  // module at a time, so the most recently answered one is the module in view.
  const questionnaireHash = latestCheckIn(points)?.questionnaireHash
  const liveDefinitions = useCareDefinitions(questionnaireHash)
  const sampleDefinitions = useMemo(() => (usingFixtures ? fixtureDefinitions() : null), [usingFixtures])
  const definitions = usingFixtures ? sampleDefinitions : liveDefinitions

  const scoped = useMemo(
    () => points.filter(point => point.questionnaireHash === questionnaireHash),
    [points, questionnaireHash]
  )

  const timeline = useMemo(() => (definitions ? toCareTimeline(scoped, definitions) : null), [scoped, definitions])
  const summaries = useMemo(() => (definitions ? toSectionSummaries(scoped, definitions) : []), [scoped, definitions])

  if (isPending && isFetching) {
    return (
      <Panel className={cn('grid place-items-center', className)}>
        <LoadingSpinner iconClassName='h-10 w-10 animate-spin text-custom-neutral-900' />
      </Panel>
    )
  }

  // The error only speaks when there is nothing to show. The same retained-`data` rule applies here:
  // a failed refetch behind cached history is not worth taking that history off screen for — the
  // prescriber keeps what was last read, and the next refetch tries again.
  if (scoped.length === 0) {
    return (
      <Panel className={className}>
        <p className='text-sm text-custom-neutral-400'>
          {isError ? 'Care data could not be loaded.' : 'This patient has no care consultations yet.'}
        </p>
      </Panel>
    )
  }

  if (!timeline || !definitions) {
    return (
      <Panel className={className}>
        <p className='text-sm text-custom-neutral-400'>
          This patient has {scoped.length} care check-in{scoped.length === 1 ? '' : 's'}, but the questionnaire
          definition needed to chart them is not available yet.
        </p>
      </Panel>
    )
  }

  return (
    <Panel className={cn('flex flex-col gap-8', className)}>
      <div className='flex flex-wrap items-center justify-between gap-4'>
        <span className='flex items-center gap-3'>
          <span className='grid h-9 w-9 place-items-center rounded-full bg-[#E6F7F5]'>
            <TrendingUp className='h-5 w-5 text-[#0FB5A5]' />
          </span>
          <h2 className='text-xl font-semibold text-custom-neutral-600'>Timeline</h2>
          {usingFixtures && (
            <span
              data-testid='care-sample-data-badge'
              title='Generated sample data — not this patient’s care history'
              className='rounded-full border border-[#F5A524] bg-[#FFF7E6] px-3 py-1 text-xs font-semibold uppercase tracking-wide text-[#B45309]'
            >
              Sample data
            </span>
          )}
        </span>

        <fieldset className='flex rounded-full border border-[#D1D9E2] p-1'>
          <legend className='sr-only'>Timeline view</legend>
          {MODES.map(option => (
            <button
              key={option.value}
              type='button'
              aria-pressed={mode === option.value}
              onClick={() => setMode(option.value)}
              className={cn(
                'cursor-pointer rounded-full px-5 py-2 text-sm transition-colors',
                mode === option.value ? 'border border-[#213FB1] font-medium text-[#213FB1]' : 'text-custom-neutral-800'
              )}
            >
              {option.label}
            </button>
          ))}
        </fieldset>
      </div>

      {usingFixtures && (
        <p className='-mt-4 rounded-2xl border border-[#F5A524] bg-[#FFF7E6] px-5 py-3 text-sm text-[#B45309]'>
          {isError
            ? 'Care history could not be loaded, so sample data is shown instead. '
            : 'This patient has no care history yet, so sample data is shown instead. '}
          None of it belongs to this patient.
        </p>
      )}

      {timeline.hasScores ? (
        <CareTimelineChart timeline={timeline} mode={mode} />
      ) : (
        <p className='rounded-2xl bg-[#FAFAFB] px-5 py-4 text-sm text-custom-neutral-400'>
          These check-ins have no scores resolved yet, so there is nothing to plot. Answers are still listed below.
        </p>
      )}

      <hr className='border-[#E9EAED]' />

      <CareSymptomTable summaries={summaries} date={latestCheckInDate(scoped)} />
    </Panel>
  )
}
